import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Building, DollarSign, Calendar, Award, CheckCircle, XCircle, Eye, Mail, Phone, MapPin, FileText, MessageSquare, Send, UserPlus, Copy, ExternalLink, MessageCircle, Search, Key, Shield, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

const PLANS = [
  { name: 'Básico', key: 'basic', price: 10, maxProps: 5 },
  { name: 'Profesional', key: 'professional', price: 25, maxProps: 15 },
  { name: 'Empresarial', key: 'enterprise', price: 50, maxProps: 9999 }
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingProperties, setPendingProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  
  // Dialogs
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [sendQuoteDialogOpen, setSendQuoteDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [viewQuoteDialogOpen, setViewQuoteDialogOpen] = useState(false);
  
  // Active tab for filtering via stat cards
  const [activeTab, setActiveTab] = useState('quotes');
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'user'|'property', id, name }
  const [deleting, setDeleting] = useState(false);

  // Support Search Dialog
  const [supportSearchOpen, setSupportSearchOpen] = useState(false);
  const [supportSearchCode, setSupportSearchCode] = useState('');
  const [supportSearchResult, setSupportSearchResult] = useState(null);
  const [supportSearching, setSupportSearching] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessReason, setAccessReason] = useState('');
  
  // Form states
  const [approvalNotes, setApprovalNotes] = useState('');
  const [quotePlan, setQuotePlan] = useState('Básico');
  const [quotePrice, setQuotePrice] = useState(10);
  const [quoteMessage, setQuoteMessage] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Acceso denegado. Solo administradores.');
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, pendingRes, allPropsRes, usersRes, quotesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/properties/pending`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/properties/all`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/quotes`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setPendingProperties(pendingRes.data);
      setAllProperties(allPropsRes.data);
      setAllUsers(usersRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Property Approval
  const handleApproval = async (approved) => {
    if (!selectedProperty) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/admin/properties/${selectedProperty.id}/approve`,
        { estado: approved ? 'aprobada' : 'rechazada', notas_admin: approvalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Propiedad ${approved ? 'aprobada' : 'rechazada'}`);
      setApprovalDialogOpen(false);
      setSelectedProperty(null);
      setApprovalNotes('');
      fetchAllData();
    } catch (error) {
      toast.error('Error al procesar');
    } finally {
      setProcessing(false);
    }
  };

  // Search property by unique code for support
  const handleSupportSearch = async () => {
    if (!supportSearchCode.trim()) {
      toast.error('Ingresa un código de propiedad');
      return;
    }
    setSupportSearching(true);
    setSupportSearchResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/properties/search/${supportSearchCode.trim().toUpperCase()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSupportSearchResult(response.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 404) {
        toast.error('No se encontró ninguna propiedad con ese código');
      } else {
        toast.error('Error al buscar propiedad');
      }
    } finally {
      setSupportSearching(false);
    }
  };

  // Request access to property for support
  const handleRequestAccess = async () => {
    if (!supportSearchResult || !accessReason.trim()) {
      toast.error('Por favor indica el motivo del acceso');
      return;
    }
    setRequestingAccess(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/properties/${supportSearchResult.id}/request-access`,
        { motivo: accessReason, duracion_horas: 24 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Solicitud de acceso enviada. El propietario debe aprobarla.');
      setAccessReason('');
      // Refresh the search result
      handleSupportSearch();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al solicitar acceso');
    } finally {
      setRequestingAccess(false);
    }
  };

  // Send Quote to Client
  const handleSendQuote = async () => {
    if (!selectedQuote) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/admin/quotes/${selectedQuote.id}/send`,
        { 
          plan_recomendado: quotePlan, 
          precio_mensual: parseFloat(quotePrice),
          mensaje_admin: quoteMessage 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Propuesta enviada al cliente');
      setSendQuoteDialogOpen(false);
      setSelectedQuote(null);
      resetQuoteForm();
      fetchAllData();
    } catch (error) {
      toast.error('Error al enviar');
    } finally {
      setProcessing(false);
    }
  };

  // Create User from Quote
  const handleCreateUser = async () => {
    if (!selectedQuote || !newUserPassword) {
      toast.error('Ingresa una contraseña para el usuario');
      return;
    }
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/quotes/${selectedQuote.id}/create-user?password=${encodeURIComponent(newUserPassword)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Usuario creado: ${response.data.email}`);
      setCreateUserDialogOpen(false);
      setSelectedQuote(null);
      setNewUserPassword('');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setProcessing(false);
    }
  };

  const resetQuoteForm = () => {
    setQuotePlan('Básico');
    setQuotePrice(10);
    setQuoteMessage('');
  };

  // Delete user or property
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (deleteTarget.type === 'user') {
        await axios.delete(`${BACKEND_URL}/api/admin/users/${deleteTarget.id}`, { headers });
        toast.success(`Usuario "${deleteTarget.name}" eliminado`);
      } else {
        await axios.delete(`${BACKEND_URL}/api/admin/properties/${deleteTarget.id}`, { headers });
        toast.success(`Propiedad "${deleteTarget.name}" eliminada`);
      }
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setDeleteDialogOpen(true);
  };

  const copyQuoteLink = (quoteId) => {
    const link = `${FRONTEND_URL}/cotizacion/${quoteId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado al portapapeles');
  };

  // Send quote via WhatsApp
  const sendViaWhatsApp = async (quote, plan = null, price = null) => {
    try {
      // FIRST: Save the quote as "enviada" in the database
      const token = localStorage.getItem('token');
      const planInfo = plan || quote.plan_recomendado || 'Básico';
      const priceInfo = price || quote.precio_mensual || '10';
      
      await axios.post(
        `${BACKEND_URL}/api/admin/quotes/${quote.id}/send`,
        { 
          plan_recomendado: planInfo, 
          precio_mensual: parseFloat(priceInfo),
          mensaje_admin: `Enviado por WhatsApp` 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Format phone number (remove spaces, dashes, and ensure it starts with country code)
      let phone = quote.telefono?.replace(/[\s\-\(\)]/g, '') || '';
      
      // If phone doesn't start with +, assume it's a local number
      if (!phone.startsWith('+')) {
        // Default to Colombia (+57) or Venezuela (+58) based on context
        if (phone.startsWith('0')) {
          phone = phone.substring(1);
        }
        // You can adjust this default country code
        phone = '57' + phone; // Colombia
      } else {
        phone = phone.substring(1); // Remove the + for wa.me link
      }

      // Get the quote link
      const quoteLink = `${FRONTEND_URL}/cotizacion/${quote.id}`;
      
      // Build the message
      const pmsType = quote.pms_type === 'apartamento' ? 'Apartamentos' : 'Hotel';
      
      const message = `¡Hola ${quote.nombre}! 👋

Gracias por tu interés en nuestro sistema PMS para *${pmsType}*.

📋 *Tu Cotización Personalizada:*
• Plan: *${planInfo}*
• Precio: *$${priceInfo} USD/mes*
• Propiedades: ${quote.num_propiedades}
• Ciudad: ${quote.ciudad}

✅ *Incluye:*
- Gestión de reservas
- Calendario unificado
- Conexión con OTAs (Booking, Airbnb)
- Dashboard de reportes
- Soporte técnico

🔗 *Revisa y acepta tu propuesta aquí:*
${quoteLink}

¿Tienes alguna pregunta? Estoy aquí para ayudarte.

_Edén - Gestión Hotelera_`;

      // Encode the message for URL
      const encodedMessage = encodeURIComponent(message);
      
      // Open WhatsApp
      const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Cotización guardada y WhatsApp abierto');
      
      // Refresh data to update the list
      fetchAllData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al preparar la cotización. Intenta de nuevo.');
    }
  };

  const getStatusBadge = (estado) => {
    const styles = {
      pendiente: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      enviada: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
      aceptada: 'bg-green-500/20 text-green-500 border-green-500/30',
      rechazada_cliente: 'bg-red-500/20 text-red-500 border-red-500/30',
      convertido: 'bg-purple-500/20 text-purple-500 border-purple-500/30'
    };
    const labels = {
      pendiente: 'Pendiente',
      enviada: 'Enviada',
      aceptada: 'Aceptada',
      rechazada_cliente: 'Rechazada',
      convertido: 'Convertido'
    };
    return <Badge variant="outline" className={styles[estado] || styles.pendiente}>{labels[estado] || estado}</Badge>;
  };

  const pendingQuotes = quotes.filter(q => q.estado === 'pendiente');
  const acceptedQuotes = quotes.filter(q => q.estado === 'aceptada');

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>Panel de Administrador</h1>
            <p className="text-muted-foreground mt-1">Gestión del sistema PMS</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Support Search Button */}
            <Button 
              variant="outline" 
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              onClick={() => setSupportSearchOpen(true)}
              data-testid="support-search-btn"
            >
              <Search className="h-4 w-4 mr-2" />
              Buscar Propiedad
            </Button>
            <Badge className="bg-amber-500 text-white px-4 py-2">
              <Award className="h-4 w-4 mr-2" />
              Administrador
            </Badge>
          </div>
        </div>

        {/* Stats - Clickable to filter tabs */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none cursor-pointer hover:scale-105 transition-transform" onClick={() => setActiveTab('users')} data-testid="stat-card-users">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white/80">Usuarios</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{stats?.total_usuarios || 0}</div></CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-none cursor-pointer hover:scale-105 transition-transform" onClick={() => setActiveTab('properties')} data-testid="stat-card-properties">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white/80">Propiedades</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{stats?.total_propiedades_sistema || 0}</div></CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white border-none cursor-pointer hover:scale-105 transition-transform" onClick={() => setActiveTab('pending')} data-testid="stat-card-pending">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white/80">Prop. Pendientes</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{stats?.propiedades_pendientes || 0}</div></CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-pink-500 to-rose-500 text-white border-none cursor-pointer hover:scale-105 transition-transform" onClick={() => setActiveTab('quotes')} data-testid="stat-card-quotes">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white/80">Cotizaciones</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{pendingQuotes.length}</div></CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white border-none cursor-pointer hover:scale-105 transition-transform" onClick={() => setActiveTab('quotes')} data-testid="stat-card-accepted">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white/80">Por Crear Usuario</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{acceptedQuotes.length}</div></CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-teal-500 text-white border-none" data-testid="stat-card-revenue">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-white/80">Ingresos/Mes</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">${stats?.ingresos_membresias_mes || 0}</div></CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="quotes" className="relative">
              Cotizaciones
              {(pendingQuotes.length + acceptedQuotes.length) > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingQuotes.length + acceptedQuotes.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending">Propiedades Pendientes</TabsTrigger>
            <TabsTrigger value="properties">Todas las Propiedades</TabsTrigger>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
          </TabsList>

          {/* Quotes Tab */}
          <TabsContent value="quotes" className="space-y-6">
            {/* Pending Quotes */}
            <div>
              <h2 className="text-xl font-semibold mb-4" style={{fontFamily: 'Manrope'}}>
                Nuevas Cotizaciones ({pendingQuotes.length})
              </h2>
              {pendingQuotes.length === 0 ? (
                <Card className="p-6 text-center text-muted-foreground">No hay cotizaciones pendientes</Card>
              ) : (
                <div className="grid gap-4">
                  {pendingQuotes.map((quote) => (
                    <Card key={quote.id} className="border-l-4 border-l-yellow-500">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold">{quote.nombre}</h3>
                              {getStatusBadge(quote.estado)}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{quote.email}</span>
                              <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{quote.telefono}</span>
                              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{quote.ciudad}</span>
                            </div>
                            <div className="flex gap-2">
                              <Badge variant="outline">{quote.tipo_propiedad}</Badge>
                              <span className="text-sm">{quote.num_propiedades} prop. | {quote.num_habitaciones_aprox} hab.</span>
                            </div>
                            {quote.mensaje && (
                              <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded mt-2">
                                <MessageSquare className="h-4 w-4 inline mr-1" />{quote.mensaje}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button onClick={() => { setSelectedQuote(quote); setSendQuoteDialogOpen(true); }}>
                              <Send className="h-4 w-4 mr-2" />Preparar Propuesta
                            </Button>
                            <Button 
                              variant="outline" 
                              className="bg-green-50 border-green-500 text-green-700 hover:bg-green-100"
                              onClick={() => sendViaWhatsApp(quote)}
                            >
                              <MessageCircle className="h-4 w-4 mr-2" />Enviar por WhatsApp
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Accepted Quotes - Need to create user */}
            {acceptedQuotes.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-4 text-green-600" style={{fontFamily: 'Manrope'}}>
                  Cotizaciones Aceptadas - Crear Usuario ({acceptedQuotes.length})
                </h2>
                <div className="grid gap-4">
                  {acceptedQuotes.map((quote) => (
                    <Card key={quote.id} className="border-l-4 border-l-green-500 bg-green-50/50">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold">{quote.nombre}</h3>
                              {getStatusBadge(quote.estado)}
                              <Badge className="bg-slate-700">{quote.plan_recomendado} - ${quote.precio_mensual}/mes</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{quote.email} | {quote.telefono}</p>
                          </div>
                          <Button className="bg-slate-800 hover:bg-slate-700" onClick={() => { setSelectedQuote(quote); setCreateUserDialogOpen(true); }}>
                            <UserPlus className="h-4 w-4 mr-2" />Crear Usuario
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* All Quotes History */}
            <div>
              <h2 className="text-xl font-semibold mb-4" style={{fontFamily: 'Manrope'}}>
                Historial de Cotizaciones ({quotes.length})
              </h2>
              <div className="grid gap-2">
                {quotes.filter(q => !['pendiente', 'aceptada'].includes(q.estado)).map((quote) => (
                  <Card key={quote.id} className="bg-muted/30">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{quote.nombre}</span>
                          {getStatusBadge(quote.estado)}
                          <span className="text-sm text-muted-foreground">{quote.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {quote.estado === 'enviada' && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => copyQuoteLink(quote.id)}>
                                <Copy className="h-3 w-3 mr-1" />Link
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="bg-green-50 border-green-500 text-green-700 hover:bg-green-100"
                                onClick={() => sendViaWhatsApp(quote)}
                              >
                                <MessageCircle className="h-3 w-3 mr-1" />WhatsApp
                              </Button>
                            </>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {new Date(quote.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Pending Properties Tab */}
          <TabsContent value="pending" className="space-y-4">
            <h2 className="text-xl font-semibold">Propiedades Pendientes ({pendingProperties.length})</h2>
            {pendingProperties.length === 0 ? (
              <Card className="p-8 text-center"><CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" /><p>No hay propiedades pendientes</p></Card>
            ) : (
              <div className="grid gap-4">
                {pendingProperties.map((property) => (
                  <Card key={property.id} className="border-l-4 border-l-amber-500">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold">{property.nombre}</h3>
                          <p className="text-sm text-muted-foreground">
                            {property.ciudad} | {property.tipo} | {property.num_habitaciones} hab. | RNT: {property.rnt}
                          </p>
                          {property.owner_info && (
                            <div className="bg-muted/50 p-2 rounded text-sm">
                              <strong>Propietario:</strong> {property.owner_info.nombre} ({property.owner_info.email})
                            </div>
                          )}
                        </div>
                        <Button onClick={() => { setSelectedProperty(property); setApprovalDialogOpen(true); }}>
                          <Eye className="h-4 w-4 mr-2" />Revisar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All Properties Tab */}
          <TabsContent value="properties" className="space-y-4">
            <h2 className="text-xl font-semibold">Todas las Propiedades ({allProperties.length})</h2>
            <div className="grid gap-2">
              {allProperties.map((property) => (
                <Card key={property.id}>
                  <CardContent className="p-4 flex justify-between items-center">
                    <div>
                      <span className="font-medium">{property.nombre}</span>
                      <Badge variant={property.estado_aprobacion === 'aprobada' ? 'default' : 'secondary'} className="ml-2">
                        {property.estado_aprobacion || 'pendiente'}
                      </Badge>
                      <p className="text-sm text-muted-foreground">{property.ciudad} | {property.owner_info?.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">{new Date(property.created_at).toLocaleDateString()}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => confirmDelete('property', property.id, property.nombre)}
                        data-testid={`delete-property-${property.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <h2 className="text-xl font-semibold">Usuarios ({allUsers.length})</h2>
            <div className="grid gap-2">
              {allUsers.map((u) => (
                <Card key={u.id}>
                  <CardContent className="p-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{u.nombre}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{u.plan || 'basic'}</Badge>
                      <span className="text-sm">{u.total_propiedades} prop.</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => confirmDelete('user', u.id, u.nombre)}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Send Quote Dialog */}
        <Dialog open={sendQuoteDialogOpen} onOpenChange={setSendQuoteDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Enviar Propuesta</DialogTitle>
              <DialogDescription>Configura el plan y precio para {selectedQuote?.nombre}</DialogDescription>
            </DialogHeader>
            {selectedQuote && (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg text-sm">
                  <p><strong>Cliente:</strong> {selectedQuote.nombre}</p>
                  <p><strong>Email:</strong> {selectedQuote.email}</p>
                  <p><strong>Propiedades:</strong> {selectedQuote.num_propiedades} | <strong>Habitaciones:</strong> {selectedQuote.num_habitaciones_aprox}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Plan Recomendado</Label>
                  <Select value={quotePlan} onValueChange={(v) => {
                    setQuotePlan(v);
                    const plan = PLANS.find(p => p.name === v);
                    if (plan) setQuotePrice(plan.price);
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLANS.map(p => (
                        <SelectItem key={p.name} value={p.name}>
                          {p.name} - ${p.price}/mes (hasta {p.maxProps === 9999 ? '∞' : p.maxProps} prop.)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Precio Mensual (USD)</Label>
                  <Input 
                    type="number" 
                    value={quotePrice} 
                    onChange={(e) => setQuotePrice(e.target.value)}
                    placeholder="10"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Mensaje para el cliente (opcional)</Label>
                  <Textarea 
                    value={quoteMessage}
                    onChange={(e) => setQuoteMessage(e.target.value)}
                    placeholder="Hola! Basado en tus necesidades, te recomendamos..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" onClick={handleSendQuote} disabled={processing}>
                    <Send className="h-4 w-4 mr-2" />
                    {processing ? 'Guardando...' : 'Guardar y generar link'}
                  </Button>
                  <Button 
                    className="bg-slate-800 hover:bg-slate-700"
                    onClick={() => {
                      sendViaWhatsApp(selectedQuote, quotePlan, quotePrice);
                      setSendQuoteDialogOpen(false);
                    }}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Enviar por WhatsApp
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  "Guardar" genera un link único. "WhatsApp" envía directamente al cliente.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Usuario</DialogTitle>
              <DialogDescription>El cliente aceptó la propuesta. Crea su cuenta.</DialogDescription>
            </DialogHeader>
            {selectedQuote && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 text-sm">
                  <p><strong>Nombre:</strong> {selectedQuote.nombre}</p>
                  <p><strong>Email:</strong> {selectedQuote.email}</p>
                  <p><strong>Plan:</strong> {selectedQuote.plan_recomendado} - ${selectedQuote.precio_mensual}/mes</p>
                </div>

                <div className="space-y-2">
                  <Label>Contraseña inicial para el usuario *</Label>
                  <Input 
                    type="text"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="Contraseña temporal"
                  />
                  <p className="text-xs text-muted-foreground">El usuario podrá cambiarla después</p>
                </div>

                <Button className="w-full bg-slate-800 hover:bg-slate-700" onClick={handleCreateUser} disabled={processing || !newUserPassword}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {processing ? 'Creando...' : 'Crear Usuario'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Property Approval Dialog */}
        <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Revisar Propiedad</DialogTitle></DialogHeader>
            {selectedProperty && (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold">{selectedProperty.nombre}</h3>
                  <p className="text-sm">Tipo: {selectedProperty.tipo} | RNT: {selectedProperty.rnt}</p>
                  <p className="text-sm">Ciudad: {selectedProperty.ciudad} | Habitaciones: {selectedProperty.num_habitaciones}</p>
                </div>
                <div className="space-y-2">
                  <Label>Notas (opcional)</Label>
                  <Textarea value={approvalNotes} onChange={(e) => setApprovalNotes(e.target.value)} rows={2} />
                </div>
                <div className="flex gap-3">
                  <Button variant="destructive" className="flex-1" onClick={() => handleApproval(false)} disabled={processing}>
                    <XCircle className="h-4 w-4 mr-2" />Rechazar
                  </Button>
                  <Button className="flex-1 bg-slate-800" onClick={() => handleApproval(true)} disabled={processing}>
                    <CheckCircle className="h-4 w-4 mr-2" />Aprobar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Support Search Dialog */}
        <Dialog open={supportSearchOpen} onOpenChange={(open) => {
          setSupportSearchOpen(open);
          if (!open) {
            setSupportSearchCode('');
            setSupportSearchResult(null);
            setAccessReason('');
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Search className="h-5 w-5 text-amber-500" />
                Buscar Propiedad para Soporte
              </DialogTitle>
              <DialogDescription>
                Ingresa el código único de la propiedad (ej: PROP-A7X3K2)
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Search Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="PROP-XXXXXX"
                  value={supportSearchCode}
                  onChange={(e) => setSupportSearchCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleSupportSearch()}
                  className="flex-1 font-mono"
                  data-testid="support-search-input"
                />
                <Button onClick={handleSupportSearch} disabled={supportSearching}>
                  {supportSearching ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>

              {/* Search Result */}
              {supportSearchResult && (
                <div className="border rounded-lg overflow-hidden">
                  {/* Property Info */}
                  <div className="p-4 bg-muted/50">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{supportSearchResult.nombre}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {supportSearchResult.ciudad}, {supportSearchResult.departamento}
                        </p>
                      </div>
                      <Badge variant="outline" className="font-mono">
                        {supportSearchResult.codigo_unico}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                      <div><strong>Tipo:</strong> {supportSearchResult.tipo}</div>
                      <div><strong>PMS:</strong> {supportSearchResult.pms_type}</div>
                      <div><strong>Habitaciones:</strong> {supportSearchResult.num_habitaciones}</div>
                      <div><strong>Estado:</strong> {supportSearchResult.estado_aprobacion}</div>
                    </div>
                  </div>

                  {/* Owner Info */}
                  {supportSearchResult.owner_info && (
                    <div className="p-4 border-t">
                      <p className="text-sm font-medium mb-2">Propietario:</p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                          {supportSearchResult.owner_info.nombre?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium">{supportSearchResult.owner_info.nombre}</p>
                          <p className="text-sm text-muted-foreground">{supportSearchResult.owner_info.email}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Access Status */}
                  <div className="p-4 border-t">
                    {supportSearchResult.acceso_activo ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                          <Shield className="h-5 w-5" />
                          <div>
                            <p className="font-medium">Acceso Activo</p>
                            <p className="text-sm">Expira: {new Date(supportSearchResult.acceso_info?.fecha_expiracion).toLocaleString()}</p>
                          </div>
                        </div>
                        <Button
                          className="w-full bg-slate-800 hover:bg-slate-700"
                          onClick={() => {
                            setSupportSearchOpen(false);
                            navigate(`/admin/property/${supportSearchResult.id}/view`);
                          }}
                          data-testid="view-property-btn"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Entrar a la Propiedad
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Para acceder a esta propiedad, debes solicitar permiso al propietario.
                        </p>
                        <Input
                          placeholder="Motivo del acceso (ej: Configuración de tarifas)"
                          value={accessReason}
                          onChange={(e) => setAccessReason(e.target.value)}
                        />
                        <Button 
                          className="w-full bg-amber-500 hover:bg-amber-600" 
                          onClick={handleRequestAccess}
                          disabled={requestingAccess || !accessReason.trim()}
                        >
                          <Key className="h-4 w-4 mr-2" />
                          {requestingAccess ? 'Enviando...' : 'Solicitar Acceso al Propietario'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget?.type === 'user'
                  ? `Se eliminará el usuario "${deleteTarget?.name}" y todas sus propiedades, reservas, pagos y datos asociados. Esta acción no se puede deshacer.`
                  : `Se eliminará la propiedad "${deleteTarget?.name}" junto con todas sus habitaciones, reservas y pagos. Esta acción no se puede deshacer.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
